import { z } from "zod";
/**
 * SOURCE DE VÉRITÉ UNIQUE des langues que l'application SAIT rendre — un
 * catalogue de traductions existe pour chacune, côté client (i18next) ET serveur
 * (catalogue de messages, I18N-4). C'est le SEUL littéral de liste : le helper
 * `getDeviceLanguage()` (mobile) et les helpers serveur le lisent tous les deux,
 * jamais une union `'fr'|'en'` dupliquée ailleurs.
 *
 * ⚠️ « supporté » ≠ « activé ». Une langue peut être supportée (catalogue
 * présent) mais NON activée : tant que la relecture native n'est pas faite, son
 * flag reste OFF (cf. `ENABLED_LANGUAGES` + gating I18N-2) et elle n'apparaît ni
 * en détection, ni au sélecteur, ni sur les surfaces serveur — on retombe sur
 * `fr` (fail-closed, INV-4). On AJOUTE une langue ici quand son catalogue existe ;
 * on l'ACTIVE séparément, langue par langue.
 *
 * `fr` est la langue SOURCE : fallback universel (INV-3), toujours en tête.
 */
export const SUPPORTED_LANGUAGES = ["fr", "en", "es", "de", "it", "pt", "nl"];
/** Langue de repli universelle (source de vérité). Toute résolution y retombe. */
export const DEFAULT_LOCALE = "fr";
/**
 * Langues ACTIVÉES par défaut (fail-closed). Baseline compile-time utilisée quand
 * aucune configuration runtime n'est encore disponible (ex. côté client avant que
 * `/v1/config` ne soit chargé). La liste runtime effective est dérivée de
 * l'environnement serveur et exposée par `/v1/config.enabledLanguages` (I18N-2) ;
 * `fr` en fait TOUJOURS partie (source de vérité), quoi qu'en dise la config.
 *
 * Au socle, seules `fr` et `en` sont activées — les nouvelles langues sont
 * supportées (squelette de catalogue présent) mais restent OFF jusqu'à relecture.
 */
export const ENABLED_LANGUAGES = ["fr", "en"];
/**
 * Ramène un tag de locale quelconque à une langue supportée, ou `null`.
 *
 * - `fr-FR`, `fr_CA`, `FR` → `fr` (on ne garde que la sous-étiquette de langue ;
 *   le multi-région fin `de-AT` vs `de-DE` est hors périmètre — D4).
 * - `de` → `de` ; `xx`, `""`, `null`, `undefined` → `null` (inconnu = rejeté,
 *   jamais deviné).
 *
 * Frontière unique par laquelle passe TOUTE locale entrant dans le système
 * (validation Zod, détection appareil, réconciliation au boot, résolution
 * serveur) : aucune locale brute non normalisée ne la franchit.
 */
export function normalizeLocale(raw) {
    if (!raw)
        return null;
    const base = raw.toLowerCase().split(/[-_]/)[0];
    return SUPPORTED_LANGUAGES.includes(base) ? base : null;
}
/** Vrai si `raw` (après normalisation) désigne une langue supportée. */
export function isSupportedLocale(raw) {
    return normalizeLocale(raw) !== null;
}
/**
 * Région par défaut d'une langue, pour piloter `Intl` (D4). Le multi-région fin
 * (`de-AT` vs `de-DE`) est hors périmètre : chaque langue a UNE région de
 * référence. Table PARTAGÉE client (format I18N-6) et serveur (format I18N-4)
 * pour qu'un e-mail et l'app formatent dates/montants à l'identique.
 */
const DEFAULT_REGION = {
    fr: "fr-FR",
    en: "en-GB", // app européenne : dates jj/mm, comme les écrans actuels
    es: "es-ES",
    de: "de-DE",
    it: "it-IT",
    pt: "pt-PT",
    nl: "nl-NL",
};
/** Balise BCP-47 d'une locale pour `Intl` (`de`→`de-DE`, `pt`→`pt-PT`). */
export function localeToBcp47(locale) {
    return DEFAULT_REGION[locale];
}
/**
 * Schéma d'une locale reçue SUR LE FIL (signup, PATCH/PUT account).
 *
 * La spec évoque `z.enum`, mais un `z.enum` strict rejetterait `fr-FR` — or un
 * appareil renvoie couramment un tag régional. On NORMALISE donc d'abord
 * (`fr-FR`→`fr`) puis on rejette ce qui n'est pas supporté : `fr-FR`→`fr` (OK),
 * `de`→`de` (OK), `xx`→**400**, jamais écrit en base. Sortie typée `Locale`.
 */
export const LocaleSchema = z.string().transform((raw, ctx) => {
    const norm = normalizeLocale(raw);
    if (norm === null) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Unsupported locale: ${raw}` });
        return z.NEVER;
    }
    return norm;
});
//# sourceMappingURL=i18n.js.map